import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

import { opacity } from "../tokens/opacity";
import { borderRadius } from "../tokens/radius";
import { shadows } from "../tokens/shadows";
import {
  avatarSize,
  badgeHeight,
  borderWidth,
  buttonHeight,
  checkboxSize,
  ctaPadding,
  dialogWidth,
  dotSize,
  fieldHeight,
  otpCellSize,
  outlineWidth,
  radioCheckSize,
  radioDotSize,
  radioSize,
  ringOffsetWidth,
  ringWidth,
  spacing,
  switchSize,
  tagHeight,
} from "../tokens/spacing";
import { fontSize, otpDigitSize } from "../tokens/typography";

// The design system registers custom font-size utilities (e.g. `text-body-sm`,
// `text-heading-h1`, plus their `-dt` desktop variants) in the Tailwind preset.
// tailwind-merge doesn't know these are font sizes, so by default it treats them
// as text-color utilities — dropping them against real color classes and failing
// to override `text-base`. Teaching it the scale makes conflict resolution correct.
const fontSizeClasses = [
  ...Object.entries(fontSize).flatMap(([category, sizes]) =>
    Object.keys(sizes).flatMap((size) => [
      `${category}-${size}`,
      `${category}-${size}-dt`,
      `size-${category}-${size}`,
    ]),
  ),
  // OTPInputField's digit (`text-size-otp-digit-md`)
  ...Object.keys(otpDigitSize).map((size) => `size-otp-digit-${size}`),
];

// Same story for the spacing scale (`p-spacing-md`, `gap-spacing-lg`, …): the
// value is a name, not a length, so tailwind-merge keeps both sides of a
// conflict and lets stylesheet order decide. Registering the scale lets a
// consumer override a component's own padding via `className`.
const spacingClasses = Object.keys(spacing).map((key) => `spacing-${key}`);

// And once more for the radius scale (`rounded-radius-md`, …): unregistered, it
// belongs to no class group, so a consumer's `rounded-radius-full` is kept next
// to the component's own radius and stylesheet order — not call order — wins.
const radiusClasses = Object.keys(borderRadius).map((key) => `radius-${key}`);

// And the Luce shadow keys (`shadow-card`, `shadow-modal`, …): unregistered,
// tailwind-merge files them as shadow colours, so a `shadow-slate-200` (a
// tint, DS-006) removed them and a `shadow-none` sat next to them.
const shadowClasses = Object.keys(shadows);

// And the CTA boxes (`w-cta-md`, `md:h-cta-md-dt`), so a consumer's `w-10`
// replaces IconButton's width instead of sitting next to it.
const ctaBoxClasses = Object.keys(ctaPadding).flatMap((key) => [
  `cta-${key}`,
  `cta-${key}-dt`,
  `cta-fab-${key}`,
  `cta-fab-${key}-dt`,
]);
// And Large's `_cta` padding (`py-cta-lg`, `md:py-cta-lg-dt`, SidebarNavItem's
// row), so a consumer's `py-0` replaces it instead of sitting next to it
const ctaPaddingClasses = ["cta-lg", "cta-lg-dt"];
// Avatar's boxes (`w-avatar-xl`, `md:w-avatar-xl-dt`), likewise
const avatarClasses = Object.keys(avatarSize).flatMap((key) => [
  `avatar-${key}`,
  `avatar-${key}-dt`,
]);
// ActionButton's pill widths (`w-action-pill-md`), likewise
const actionPillClasses = Object.keys(ctaPadding).flatMap((key) => [
  `action-pill-${key}`,
  `action-pill-${key}-dt`,
]);

// And the named widths (register row 21): `ring-focus`, `ring-switch-halo`,
// `ring-offset-focus` and `border-1-5`. Unregistered, tailwind-merge files them
// as colours, so `ring-luce-brand-primaryBorder` removed the focus ring's width.
const ringWidthClasses = Object.keys(ringWidth);
const ringOffsetWidthClasses = Object.keys(ringOffsetWidth);
const borderWidthClasses = Object.keys(borderWidth);
// Divider's strokes (`h-outline-2`) and Badge's dot (`size-badge-dot`): no
// group, so a caller's `h-1` sat next to them
const outlineClasses = Object.keys(outlineWidth).map((key) => `outline-${key}`);
const dotClasses = Object.keys(dotSize);

// And the control boxes (round 2): `h-button-md`, `md:h-checkbox-md-dt`,
// `size-switch-knob-md`, … Unregistered, they're in no group, so a caller's
// `h-10` sits next to `h-button-md` and stylesheet order decides
const withDesktop = (name: string, scale: object) =>
  Object.keys(scale).flatMap((key) => [`${name}-${key}`, `${name}-${key}-dt`]);
const squareBoxClasses = [
  ...withDesktop("checkbox", checkboxSize),
  ...withDesktop("radio", radioSize),
  ...withDesktop("radio-dot", radioDotSize),
  ...withDesktop("radio-check", radioCheckSize),
  ...withDesktop("otp-cell", otpCellSize),
  ...Object.keys(switchSize).map((key) => `switch-${key}`),
];
const heightClasses = [
  ...withDesktop("button", buttonHeight),
  ...withDesktop("field", fieldHeight),
  ...withDesktop("tag", tagHeight),
  ...withDesktop("badge", badgeHeight),
];
const badgeClasses = withDesktop("badge", badgeHeight);
const switchKnobClasses = Object.keys(switchSize).map((key) => `switch-knob-${key}`);
const switchKnobOffsetClasses = Object.keys(switchSize).flatMap((key) => [
  `switch-knob-off-${key}`,
  `switch-knob-on-${key}`,
]);
// The overlays' named widths (B9.8b): `max-w-dialog-md`, `max-w-tooltip` and
// the sheet handle's `w-`/`h-sheet-handle`. Unregistered, a caller's
// `md:max-w-[343px]` (the promo pop-up's) sat next to Dialog's 400 and lost
const maxWidthClasses = [...Object.keys(dialogWidth).map((key) => `dialog-${key}`), "tooltip"];
// `opacity-disabled`, likewise: a caller's `opacity-0` sat next to it. (The
// `ring-halo` width is in `ringWidthClasses`.)
const opacityClasses = Object.keys(opacity);

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [{ text: fontSizeClasses }],
      rounded: [{ rounded: radiusClasses }],
      // and every corner and side, e.g. TagRibbon's `rounded-tl-radius-lg`
      ...Object.fromEntries(
        ["s", "e", "t", "r", "b", "l", "ss", "se", "ee", "es", "tl", "tr", "br", "bl"].map(
          (side) => [`rounded-${side}`, [{ [`rounded-${side}`]: radiusClasses }]],
        ),
      ),
      shadow: [{ shadow: shadowClasses }],
      "ring-w": [{ ring: ringWidthClasses }],
      "ring-offset-w": [{ "ring-offset": ringOffsetWidthClasses }],
      "border-w": [{ border: borderWidthClasses }],
      // and every side, e.g. `border-b-1-5`
      ...Object.fromEntries(
        ["x", "y", "s", "e", "t", "r", "b", "l"].map((side) => [
          `border-w-${side}`,
          [{ [`border-${side}`]: borderWidthClasses }],
        ]),
      ),
      w: [
        {
          w: [
            ...ctaBoxClasses,
            ...actionPillClasses,
            ...avatarClasses,
            ...outlineClasses,
            ...squareBoxClasses,
            "sheet-handle",
          ],
        },
      ],
      h: [
        {
          h: [
            ...ctaBoxClasses,
            ...avatarClasses,
            ...outlineClasses,
            ...squareBoxClasses,
            ...heightClasses,
            "sheet-handle",
          ],
        },
      ],
      "max-w": [{ "max-w": maxWidthClasses }],
      "min-w": [{ "min-w": badgeClasses }],
      "min-h": [{ "min-h": ["touch-target"] }],
      size: [{ size: [...dotClasses, ...switchKnobClasses, "touch-target"] }],
      "translate-x": [{ "translate-x": switchKnobOffsetClasses }],
      "translate-y": [{ "translate-y": spacingClasses }],
      left: [{ left: spacingClasses }],
      opacity: [{ opacity: opacityClasses }],
      p: [{ p: [...spacingClasses, ...ctaPaddingClasses] }],
      px: [{ px: [...spacingClasses, ...ctaPaddingClasses] }],
      py: [{ py: [...spacingClasses, ...ctaPaddingClasses] }],
      pt: [{ pt: [...spacingClasses, ...ctaPaddingClasses] }],
      pr: [{ pr: [...spacingClasses, ...ctaPaddingClasses] }],
      pb: [{ pb: [...spacingClasses, ...ctaPaddingClasses] }],
      pl: [{ pl: [...spacingClasses, ...ctaPaddingClasses] }],
      m: [{ m: spacingClasses }],
      mx: [{ mx: spacingClasses }],
      my: [{ my: spacingClasses }],
      mt: [{ mt: spacingClasses }],
      mr: [{ mr: spacingClasses }],
      mb: [{ mb: spacingClasses }],
      ml: [{ ml: spacingClasses }],
      gap: [{ gap: spacingClasses }],
      "gap-x": [{ "gap-x": spacingClasses }],
      "gap-y": [{ "gap-y": spacingClasses }],
    },
  },
});

/**
 * Merges class names using clsx and tailwind-merge.
 * Handles conditional classes and resolves Tailwind CSS conflicts.
 *
 * @example
 * cn("p-4", "p-2") // => "p-2"
 * cn("text-red-500", isActive && "text-blue-500") // => conditional
 * cn(buttonVariants({ variant: "primary" }), className) // => with cva
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
